/**
 * A sign-in a machine is waiting on: the vendor's own page, the code to enter there, and, when the
 * vendor's login waits for something back (Claude's code, Google's loopback address), a place to
 * paste it. The machine finishes the sign-in; nothing here signs anyone in.
 */

import type { SignInWants } from '@aioe/shared';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { Button, ErrorNote, inputClass } from './ui.tsx';

/** `answer: false` leaves the answer to the caller (the inbox shows it across the card). */
export function SignInLink({ label, params, answer = true }: { label: string; params?: Record<string, unknown>; answer?: boolean }) {
  const url = typeof params?.url === 'string' && params.url.startsWith('https://') ? params.url : undefined;
  const code = typeof params?.code === 'string' ? params.code : undefined;
  const wants = params?.wants === 'code' || params?.wants === 'redirect-url' ? (params.wants as SignInWants) : undefined;
  const signInId = typeof params?.signInId === 'string' ? params.signInId : undefined;
  if (!url) return null;
  return (
    <span className="grid gap-2">
      <span className="inline-flex flex-wrap items-center gap-2">
        {code && <span className="mono rounded-md bg-surface-secondary px-2 py-1 text-sm tracking-widest">{code}</span>}
        <Button
          size="sm"
          onPress={() => {
            // The code goes on the clipboard for the vendor's page.
            if (code) void navigator.clipboard?.writeText(code).catch(() => undefined);
            window.open(url, '_blank', 'noopener');
          }}
        >
          {label}
        </Button>
      </span>
      {answer && wants && signInId && <SignInAnswer id={signInId} wants={wants} />}
    </span>
  );
}

/** What the vendor's page gave the person, sent back to the machine that is waiting for it. */
export function SignInAnswer({ id, wants, answered, onSent }: { id: string; wants: SignInWants; answered?: boolean; onSent?: () => void }) {
  const [value, setValue] = useState('');
  const [sent, setSent] = useState(Boolean(answered));
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  if (sent) return <span className="text-xs text-muted">Sent. The machine is finishing the sign-in.</span>;
  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.answerSignIn(id, value.trim());
      setSent(true);
      onSent?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="grid gap-1.5">
      <label className="grid gap-1 text-xs text-muted">
        {wants === 'code' ? 'After you sign in, the page shows a code. Paste it here.' : 'After you sign in, your browser goes to an address that does not load. That is expected: copy the whole address and paste it here.'}
        <input className={`${inputClass} mono`} autoComplete="off" value={value} onChange={(e) => setValue(e.target.value)} placeholder={wants === 'code' ? 'The code from the page' : 'http://127.0.0.1:…'} />
      </label>
      <span>
        <Button size="sm" variant="secondary" isPending={busy} isDisabled={value.trim().length < 4} onPress={() => void send()}>
          Send to the machine
        </Button>
      </span>
      <ErrorNote error={error} />
    </span>
  );
}

/** The answer a sign-in action waits for, if any: what the inbox shows under the record. */
export function signInWaitsFor(params?: Record<string, unknown>): { id: string; wants: SignInWants } | undefined {
  const wants = params?.wants === 'code' || params?.wants === 'redirect-url' ? (params.wants as SignInWants) : undefined;
  return wants && typeof params?.signInId === 'string' ? { id: params.signInId, wants } : undefined;
}
